<script setup lang="ts">
import { connect as connectImage } from "~/connectors/fragments/WrapperImage"
import { discountPercentage } from "~/utils/formatters"
import type { CardProps } from "../ProductTile/CartPreview/Card.props"
import { Bundle, Sku } from "@commercelayer/sdk"
import { LineItem_ } from "~/composables/useOrder"
import { checkImage } from "~/utils/checkImage"
import { useAlgoliaMappedProducts } from "~/composables/useAlgoliaMappedProducts"
import { calculateBundleLineItemAvailability } from "~/utils/product"

const {
  freeGiftPromotions,
  lineItems,
  shippingInfos,
  decreaseQuantity,
  addToCart
} = useCart()

const { mapCartProductsFactory } = useMapCart()

const { removeQuantityFromCart, updateCartList } = useGA4SiteEvents()

const { data: result } = useLazyFetch<any>(
  `/api/algolia/getRecommendations/recommend-4/param`,
  {
    server: false
  }
)

defineEmits<{ (e: "onClose"): void }>()

const mapMiniCartProduct = (
  item: LineItem_<Sku | Bundle>
): Omit<CardProps, "isFreeGift" | "quantity"> => ({
  id: item.id,
  productImage: connectImage(checkImage(item?.metadata?.product_image)),
  title: item.name!,
  discountPercetage: item.bundle_code
    ? discountPercentage(item?.metadata?.price, item?.metadata?.oldPrice)
    : discountPercentage(
        item.item.prices![0].amount_float!,
        item.item.prices![0].compare_at_amount_float!
      ),
  oldPrice: item.bundle_code
    ? item?.metadata?.oldPrice
    : item.item.prices![0].compare_at_amount_float,
  currentPrice: item.bundle_code
    ? item?.metadata?.price
    : item.item.prices![0].amount_float!,
  path: item?.metadata?.slug,
  productCode: item.bundle_code ? item.bundle_code! : item.sku_code!,
  inStock: item.bundle_code
    ? calculateBundleLineItemAvailability(item)
    : item?.item?.inventory?.available
})

const miniCartProducts = computed(() =>
  mapCartProductsFactory(mapMiniCartProduct)(freeGiftPromotions.value)(
    lineItems.value
  )
)

onMounted(() => {
  updateCartList()
})

watch(lineItems, updateCartList)

const onRemoveFromCartEvent = (
  id: string,
  productCode: string,
  quantity: number
) => {
  decreaseQuantity(id, quantity)

  removeQuantityFromCart(productCode, {
    quantity
  })
}

const suggestionProducts = useAlgoliaMappedProducts(result)
</script>

<template>
  <TheMiniCart
    @onClose="$emit('onClose')"
    @onRemoveFromCart="
      ({ id, quantity, productCode }) =>
        onRemoveFromCartEvent(id, productCode, quantity)
    "
    :cartProducts="miniCartProducts"
    :recommendedProducts="suggestionProducts"
    :freeShipmentOver="shippingInfos?.shippingInfos?.value?.freeShipping"
  />
</template>
